import React, { useEffect, useState } from "react";
import { Dialog, Box, Avatar, Typography, IconButton, Divider, TextField, Button, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import ApiService from "../../services/api-service";
import { getUserDetail } from "../../constants/function";
import notifier from "../../components/Notifier";
import GalleryComments from "./GalleryComments";
import ImageCarousel from "./ImageCarousel";

const GOLD = "#FFEE20";
const PANEL_W = 380;

function timeAgo(value) {
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  } catch {
    return "";
  }
}

// Resolve poster display info. The list endpoint populates `user`, but a
// freshly uploaded item may only carry the id — fall back to the local user.
function resolvePoster(item, currentUser) {
  const u = item && item.user;
  if (u && typeof u === "object" && u.firstName) return u;
  if (currentUser && u && String(currentUser._id) === String(u)) return currentUser;
  return typeof u === "object" ? u : null;
}

function posterName(user) {
  if (!user) return "Unknown";
  return `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Unknown";
}

// Instagram-style expanded view for a single gallery photo.
// Web: scrollable details (poster, caption, likes, comments) on the LEFT,
// image on the RIGHT. Mobile: image on top, details below.
// `onItemUpdate(itemId, patch)` lets the grid reflect like/comment changes.
export default function GalleryDetailModal({ item, open, onClose, onItemUpdate }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const currentUser = getUserDetail();

  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [commentsCount, setCommentsCount] = useState(0);
  const [caption, setCaption] = useState("");
  const [editingCaption, setEditingCaption] = useState(false);
  const [captionDraft, setCaptionDraft] = useState("");

  useEffect(() => {
    if (item) {
      setLiked(Boolean(item.isLiked));
      setLikesCount(item.likesCount || 0);
      setCommentsCount(item.commentsCount || 0);
      setCaption(item.caption || "");
    }
  }, [item]);

  if (!item) return null;

  const poster = resolvePoster(item, currentUser);
  const isOwner = currentUser && poster && String(currentUser._id) === String(poster._id);
  const goToProfile = () => {
    const id = poster && poster._id;
    if (id) window.location.href = `/artist/${id}`;
  };

  const saveCaption = async () => {
    const next = captionDraft.slice(0, 500);
    try {
      await ApiService.GALLERY_UPDATE(item._id, { caption: next });
      setCaption(next);
      setEditingCaption(false);
      if (onItemUpdate) onItemUpdate(item._id, { caption: next });
    } catch (e) {
      notifier.error("Failed to update description");
    }
  };

  const handleTogglePhotoLike = async () => {
    const next = !liked;
    setLiked(next);
    setLikesCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      const res = await ApiService.GALLERY_TOGGLE_LIKE(item._id);
      const data = (res && (res.result || res.data)) || null;
      if (data && typeof data.likesCount === "number") {
        setLiked(Boolean(data.liked));
        setLikesCount(data.likesCount);
        if (onItemUpdate) onItemUpdate(item._id, { isLiked: data.liked, likesCount: data.likesCount });
      }
    } catch (e) {
      setLiked(!next);
      setLikesCount((c) => Math.max(0, c + (next ? -1 : 1)));
    }
  };

  const handleCommentCountChange = (delta) => {
    setCommentsCount((c) => Math.max(0, c + delta));
    if (onItemUpdate) onItemUpdate(item._id, { commentsCount: Math.max(0, commentsCount + delta) });
  };

  const imageList =
    item.images && item.images.length
      ? item.images.filter((im) => im && im.filePath)
      : item.filePath
      ? [{ filePath: item.filePath }]
      : [];

  const imagePanel = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        bgcolor: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: isMobile ? "44vh" : "auto",
        maxHeight: isMobile ? "60vh" : "none",
      }}
    >
      {imageList.length > 0 ? (
        <ImageCarousel
          images={imageList}
          alt={item.caption || ""}
          imgStyle={{ maxHeight: isMobile ? "60vh" : "88vh" }}
        />
      ) : (
        <Box sx={{ color: "#555", p: 4 }}>Image unavailable</Box>
      )}
    </Box>
  );

  const detailsPanel = (
    <Box
      sx={{
        width: isMobile ? "100%" : PANEL_W,
        flexShrink: 0,
        height: isMobile ? "auto" : "100%",
        overflowY: isMobile ? "visible" : "auto",
        borderRight: isMobile ? "none" : "1px solid #1c1c24",
        display: "flex",
        flexDirection: "column",
        p: 2,
      }}
    >
      {/* Poster header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <Avatar
          src={poster?.profileImg?.filePath}
          sx={{ width: 40, height: 40, cursor: poster?._id ? "pointer" : "default" }}
          onClick={goToProfile}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontSize: 15, fontWeight: 700, color: "#fff", cursor: poster?._id ? "pointer" : "default" }}
            onClick={goToProfile}
          >
            {posterName(poster)}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#888" }}>{timeAgo(item.createdAt)}</Typography>
        </Box>
      </Box>

      {/* Caption (owner can edit) */}
      {editingCaption ? (
        <Box sx={{ mb: 1.5 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            size="small"
            value={captionDraft}
            onChange={(e) => setCaptionDraft(e.target.value)}
            inputProps={{ maxLength: 500 }}
            placeholder="Add a description…"
            sx={{
              "& .MuiOutlinedInput-root": { bgcolor: "#1a1a1a", color: "#fff", borderRadius: "10px" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#2a2a2a" },
            }}
          />
          <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
            <Button size="small" onClick={() => setEditingCaption(false)} sx={{ color: "#aaa", textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={saveCaption}
              sx={{ background: GOLD, color: "#000", textTransform: "none", "&:hover": { background: "#FFE066" } }}
            >
              Save
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: 1.5 }}>
          {caption ? (
            <Typography sx={{ fontSize: 14, color: "#ddd", whiteSpace: "pre-wrap", wordBreak: "break-word", flex: 1 }}>
              {caption}
            </Typography>
          ) : (
            isOwner && <Typography sx={{ fontSize: 14, color: "#777", flex: 1 }}>Add a description…</Typography>
          )}
          {isOwner && (
            <IconButton
              size="small"
              onClick={() => {
                setCaptionDraft(caption);
                setEditingCaption(true);
              }}
              sx={{ color: "#888" }}
            >
              <EditOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      )}

      {/* Like + comment counts */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton size="small" onClick={handleTogglePhotoLike} sx={{ color: liked ? "#ff4d6d" : "#bbb" }}>
            {liked ? <FavoriteIcon sx={{ fontSize: 22 }} /> : <FavoriteBorderIcon sx={{ fontSize: 22 }} />}
          </IconButton>
          <Typography sx={{ fontSize: 14, color: "#ccc" }}>{likesCount}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 19, color: "#bbb" }} />
          <Typography sx={{ fontSize: 14, color: "#ccc" }}>{commentsCount}</Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "#1c1c24", mb: 1.5 }} />

      {/* Comments */}
      <Box sx={{ flex: 1 }}>
        <GalleryComments galleryId={item._id} onCountChange={handleCommentCountChange} />
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={false}
      PaperProps={{
        sx: {
          bgcolor: "#0d0d12",
          color: "#fff",
          m: isMobile ? 0 : 2,
          width: isMobile ? "100%" : "min(1000px, 94vw)",
          height: isMobile ? "100%" : "90vh",
          maxWidth: "none",
          borderRadius: isMobile ? 0 : "14px",
          overflow: "hidden",
          position: "relative",
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 2,
          color: "#fff",
          bgcolor: "rgba(0,0,0,0.45)",
          "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
        }}
      >
        <CloseIcon />
      </IconButton>

      {isMobile ? (
        <Box sx={{ height: "100%", overflowY: "auto" }}>
          {imagePanel}
          {detailsPanel}
        </Box>
      ) : (
        <Box sx={{ display: "flex", height: "100%" }}>
          {detailsPanel}
          {imagePanel}
        </Box>
      )}
    </Dialog>
  );
}
